/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Accordion, Container, Row, Col, useAccordionButton } from "react-bootstrap";
import AccordionContext from "react-bootstrap/AccordionContext";
import { Button, Card, FieldGroup, Select } from "core/components";
import { Controller, useFormContext } from "react-hook-form";
import { TaskFormData } from "core/schema";
import TemplateContext from "./TemplateContext";
import TemplateTasksContext from "./TemplateTasksContext";
import TemplateTaskItemsForm from "./TemplateTaskItemsForm";
import { IMaskInput } from "react-imask";

function CustomToggle({ eventKey }) {
  const accordionContext = React.useContext(AccordionContext);
  const decoratedOnClick = useAccordionButton(eventKey);

  const isOpen = accordionContext.activeEventKey?.includes(eventKey);
  const icon = isOpen ? "chevron-up" : "chevron-down";

  return <Button className={"button--default"} type="button" onClick={decoratedOnClick} icon={icon} iconProps={{ className: "svg-icon h5" }} />;
}

const TemplateTaskForm = ({ index, remove, is_purchasing, tasks, items }): JSX.Element => {
  const { setTasksForDestruction } = React.useContext(TemplateContext);
  const { selectedTasks, setSelectedTasks } = React.useContext(TemplateTasksContext);
  const {
    register,
    control,
    watch,
    resetField,
    formState: { errors }
  } = useFormContext();
  const fieldArrayName = "template_tasks_attributes";
  const field = `${fieldArrayName}.[${index}]`;
  const templateTask = watch(field);
  const [selectedTask, setSelectedTask] = React.useState<TaskFormData>(tasks.find((task) => task.id == templateTask?.task_id));

  const addToSelectedTasks = (task) => {
    if (selectedTasks.find((i) => i?.id == task?.id)) {
      return;
    } else {
      setSelectedTasks((value) => [...value, task]);
    }
  };

  const removeFromSelectedTasks = (task) => {
    if (selectedTasks.find((selectedTask) => selectedTask?.id == task?.id)) {
      setSelectedTasks(selectedTasks.filter((selectedTask) => selectedTask?.id != task.id) || []);
    }
  };

  React.useEffect(() => {
    if (selectedTask) {
      addToSelectedTasks(selectedTask);
    }
  }, [selectedTask, selectedTasks]);

  return (
    <React.StrictMode>
      <div className="card-template">
        <Card className="card">
          <input hidden={true} {...register(`template_tasks_attributes.${index}.id` as const)} />
          <input hidden={true} {...register(`template_tasks_attributes.${index}.template_id` as const)} />
          <Card.Header
            title={selectedTask ? `${selectedTask?.task_classification?.name} | ${selectedTask.code} - ${selectedTask.name}` : "New Task"}
            titleProps={{ className: "h5" }}
          >
            {errors.template_tasks_attributes?.[index] && <Button className="mr-2 button--danger" icon="exclamation-triangle" />}
            {!is_purchasing && (
              <Button
                className="mr-2 button--danger"
                icon="minus"
                iconProps={{ className: "svg-icon h5" }}
                onClick={() => {
                  if (templateTask?.id) {
                    setTasksForDestruction((value) => [...value, { ...templateTask, _destroy: "1" }]);
                  }
                  resetField(field, { defaultValue: {} });
                  removeFromSelectedTasks(selectedTask);
                  remove(index);
                }}
              />
            )}
            <CustomToggle eventKey={index.toString()} />
          </Card.Header>
          <Accordion.Collapse eventKey={index.toString()}>
            <Card.Body>
              <Container fluid>
                <Row>
                  <Col>
                    <Controller
                      name={`${fieldArrayName}.${index}.task_id`}
                      control={control}
                      render={({ field }) => (
                        <Select
                          label="Task"
                          placeholder="Select a Task"
                          required
                          noOptionsMessage={() => "No available task."}
                          options={tasks.filter(
                            (task) => !selectedTasks.find((selected) => selected?.id == task?.id && field?.value != task?.id) || null
                          )}
                          getOptionLabel={(option) => `${option.code} - ${option.name}`}
                          getOptionValue={(option) => option.id}
                          isOptionDisabled={(option) => !option.active}
                          onChange={(option, actionMeta) => {
                            field.onChange(option?.id);

                            if (selectedTask) {
                              removeFromSelectedTasks(selectedTask);
                            }

                            setSelectedTask(option);

                            if (actionMeta.action == "select-option") {
                              addToSelectedTasks(option);
                            } else {
                              removeFromSelectedTasks(option || actionMeta?.removedValues[0]);
                            }
                          }}
                          ref={field.ref}
                          disabled={is_purchasing}
                          value={tasks.find((task) => task.id == field.value) || null}
                          errorMessage={errors.template_tasks_attributes?.[index]?.task_id?.message}
                        />
                      )}
                    />
                  </Col>
                  <Col>
                    <Controller
                      name={`${fieldArrayName}.${index}.weight`}
                      control={control}
                      render={({ field }) => (
                        <FieldGroup label="Weight" required errorMessage={errors.template_tasks_attributes?.[index]?.weight?.message}>
                          <IMaskInput
                            mask={Number}
                            padFractionalZeros={true}
                            thousandsSeparator=","
                            radix="."
                            value={field.value?.toString()}
                            unmask={true}
                            inputRef={field.ref}
                            onAccept={(value) => {
                              field.onChange(value);
                            }}
                            className="field"
                            placeholder="0.0"
                            min={0}
                            max={100}
                            readOnly={is_purchasing}
                          />
                        </FieldGroup>
                      )}
                    />
                  </Col>
                  <Col>
                    <Controller
                      name={`${fieldArrayName}.${index}.prerequisite_task_id`}
                      control={control}
                      render={({ field }) => (
                        <Select
                          label="Prerequisite Task"
                          placeholder="Select a Prequisite Task"
                          noOptionsMessage={() => "No available prerequisite task."}
                          options={selectedTasks}
                          formatOptionLabel={(option) => `${option.code} - ${option.name}`}
                          getOptionLabel={(option) => option.name}
                          getOptionValue={(option) => option.id}
                          isOptionDisabled={(option) => !option.active || selectedTask?.id == option.id}
                          onChange={(option) => {
                            field.onChange(option?.id);
                          }}
                          disabled={is_purchasing}
                          value={selectedTasks.find((selectedTask) => selectedTask?.id == field.value) || null}
                          errorMessage={errors.template_tasks_attributes?.[index]?.prerequisite_task_id?.message}
                        />
                      )}
                    />
                  </Col>
                </Row>
                <TemplateTaskItemsForm parentIndex={index} task={selectedTask} is_purchasing={is_purchasing} items={items} />
              </Container>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </div>
    </React.StrictMode>
  );
};

export default TemplateTaskForm;
